import React, { useState} from 'react';
import { useParams } from 'react-router-dom';
import { 
  getProjectById, 
  analyzeProject, 
  retryFailedAnalyses, 
  Project,
  getAnalysisStats
} from '../services/projectService';
import { createContext } from '../services/contextService';
import AddContextModal from '../components/AddContextModal';
import ContextList from '../components/ContextList';
import AnalysisResultsList from '../components/AnalysisResultsList';
import AnalysisStats from '../components/AnalysisStats';
import ProgressBar from '../components/ProgressBar';
import LoadingSpinner from '../components/LoadingSpinner';
import { FaFileAlt, FaClipboardList, FaPaintBrush, FaPlus, FaPlay, FaSync, FaBolt, FaCalendarAlt, FaProjectDiagram } from 'react-icons/fa';
import Tooltip from '../components/Tooltip';
import './ProjectDetailsPage.css';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

const ProjectDetailsPage = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // --- Data Fetching with TanStack Query ---
  const { data: project, isLoading, error } = useQuery<Project, Error>({
    queryKey: ['project', projectId],
    queryFn: () => getProjectById(projectId!),
    enabled: !!projectId, // Ensure query only runs when projectId is available
  });

  const { data: analysisStats, isLoading: isLoadingStats } = useQuery({
    queryKey: ['analysisStats', projectId],
    queryFn: () => getAnalysisStats(projectId!),
    enabled: !!projectId,
  });

  // --- Mutations for Actions ---
  const analyzeMutation = useMutation({
    mutationFn: () => analyzeProject(projectId!),
    onSuccess: (result) => {
      alert(result.message);
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
    },
    onError: (err: any) => {
      console.error('Failed to start analysis:', err);
      const errorMessage = err.response?.data?.detail || 'An unknown error occurred.';
      alert(`Error: ${errorMessage}`);
    }
  });

  const retryMutation = useMutation({
    mutationFn: () => retryFailedAnalyses(projectId!),
    onSuccess: (result) => {
      alert(result.message);
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
    },
    onError: (err: any) => {
      console.error('Failed to retry failed analyses:', err);
      const errorMessage = err.response?.data?.detail || 'An unknown error occurred while retrying.';
      alert(`Error: ${errorMessage}`);
    }
  });

  const addContextMutation = useMutation({
    mutationFn: (context: { name: string; type: string; content: string | File }) => {
      if (context.type === 'plaintext') {
        return createContext(projectId!, { name: context.name, type: context.type, text: context.content as string });
      } else if (context.type === 'url') {
        return createContext(projectId!, { name: context.name, type: context.type, source: context.content as string });
      }
      // Placeholder for file uploads
      return Promise.reject(new Error(`Submission for '${context.type}' is not implemented yet.`));
    },
    onSuccess: (newContext) => {
      alert('Context successfully submitted!');
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      setIsModalOpen(false);
    },
    onError: (err: any) => {
      console.error('Failed to create context:', err);
      alert('Failed to create context. Please check the console for more details.');
    }
  });

  const [isModalOpen, setIsModalOpen] = useState(false);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return <div className="project-details-page"><h2 className="error-message">{error.message}</h2></div>;
  }

  if (!project) {
    return <div className="project-details-page"><h2>Project not found.</h2></div>;
  }

  const formattedDate = new Date(project.creation_date).toLocaleDateString();

  return (
    <div className="project-details-page">
      <header className="project-header-v2">
        <div className="ph2__top-row">
          <div className="ph2__identity">
            <div className="ph2__icon">
              <FaProjectDiagram />
            </div>
            <div className="ph2__text">
              <h1 className="ph2__title">{project.name}</h1>
              <div className="ph2__meta-row">
                <span className="ph2__date">
                  <FaCalendarAlt className="ph2__date-icon" />
                  {formattedDate}
                </span>
                <span className="ph2__separator">|</span>
                <span className="ph2__item-count">
                  {project.stats?.total ?? 0} items
                </span>
              </div>
            </div>
          </div>
          <span className={`ph2__status-badge ph2__status--${project.state.toLowerCase().replace(/ /g, '_')}`}>
            <span className="ph2__status-dot"></span>
            {project.state}
          </span>
        </div>

        {project.stats && project.stats.total > 0 && (
          <div className="ph2__progress-section">
            <ProgressBar
              total={project.stats.total}
              completed={project.stats.completed}
              analyzing={project.stats.analyzing}
              failed={project.stats.failed}
            />
          </div>
        )}
      </header>

      <AnalysisStats stats={analysisStats || null} isLoading={isLoadingStats} projectId={projectId || ''} />

      <div className="section-divider"></div>

      <div className="section-header">
        <FaBolt size="1.5rem" />
        <h2>Actions</h2>
        <Tooltip text="Quick actions for managing contexts and running AI analysis on this project." />
      </div>

      <div className="action-cards-grid">
        <div
          className="action-card accent-blue"
          role="button"
          tabIndex={0}
          onClick={() => setIsModalOpen(true)}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setIsModalOpen(true); }}}
        >
          <div className="action-card-icon bg-blue"><FaPlus /></div>
          <div className="action-card-content">
            <span className="action-card-title">
              Add Context
              <Tooltip text="Add company-specific information such as text descriptions or URLs." />
            </span>
            <span className="action-card-description">Add company info to define scope</span>
          </div>
        </div>

        <div
          className="action-card accent-purple"
          role="button"
          tabIndex={0}
          onClick={() => navigate(`/projects/${projectId}/canvas`)}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/projects/${projectId}/canvas`); }}}
        >
          <div className="action-card-icon bg-purple"><FaPaintBrush /></div>
          <div className="action-card-content">
            <span className="action-card-title">
              Open Canvas
              <Tooltip text="Visual workspace for reviewing and editing the compliance matrix." />
            </span>
            <span className="action-card-description">Visual compliance matrix editor</span>
          </div>
        </div>

        <div
          className={`action-card accent-green${analyzeMutation.isPending || project?.state !== 'Pendiente' ? ' disabled' : ''}`}
          role="button"
          tabIndex={analyzeMutation.isPending || project?.state !== 'Pendiente' ? -1 : 0}
          onClick={() => { if (!analyzeMutation.isPending && project?.state === 'Pendiente') analyzeMutation.mutate(); }}
          onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && !analyzeMutation.isPending && project?.state === 'Pendiente') { e.preventDefault(); analyzeMutation.mutate(); }}}
        >
          <div className="action-card-icon bg-green"><FaPlay /></div>
          <div className="action-card-content">
            <span className="action-card-title">
              {analyzeMutation.isPending ? 'Starting Analysis...' : (project?.state === 'Pendiente' ? 'Analyze Project' : 'Analysis in Progress')}
              <Tooltip text="Run AI analysis to assess applicability of regulations against project contexts." />
            </span>
            <span className="action-card-description">Run AI compliance analysis</span>
          </div>
        </div>

        {project.stats && project.stats.failed > 0 && (
          <div
            className={`action-card accent-red${retryMutation.isPending ? ' disabled' : ''}`}
            role="button"
            tabIndex={retryMutation.isPending ? -1 : 0}
            onClick={() => { if (!retryMutation.isPending) retryMutation.mutate(); }}
            onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && !retryMutation.isPending) { e.preventDefault(); retryMutation.mutate(); }}}
          >
            <div className="action-card-icon bg-red"><FaSync /></div>
            <div className="action-card-content">
              <span className="action-card-title">
                {retryMutation.isPending ? 'Retrying...' : 'Retry Failed'}
                <Tooltip text="Retry analysis items that failed due to transient errors." />
              </span>
              <span className="action-card-description">Re-process failed analysis items</span>
            </div>
          </div>
        )}
      </div>

      <div className="section-divider"></div>

      <div className="section-header">
        <FaFileAlt size="1.5rem" />
        <h2>Contexts</h2>
        <Tooltip text="Company-specific information that defines the business scope for analysis." />
      </div>
      <ContextList contexts={project.contexts || []} onAddContext={() => setIsModalOpen(true)} />

      <div className="section-divider"></div>

      <div className="section-header">
        <FaClipboardList size="1.5rem" />
        <h2>Analysis Results</h2>
        <Tooltip text="AI-generated analysis results assessing regulation applicability and risk." />
      </div>
      <AnalysisResultsList
        results={project.analysis_results || []}
        projectName={project.name}
        projectId={projectId || ''}
      />

      <AddContextModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={(context) => addContextMutation.mutate(context)}
        projectId={projectId || ''}
      />
    </div>
  );
};

export default ProjectDetailsPage; 